import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Video } from "lucide-react";
import api from "../api/client";
import BehaviorNav from "./BehaviorNav";

const RANGES = ["7d", "30d", "90d"];

export default function BehaviorSessions({ selectedSite }) {
  const [sessions, setSessions] = useState([]);
  const [range, setRange] = useState("7d");
  const [device, setDevice] = useState("");
  const [hasRecording, setHasRecording] = useState("");
  const [bounced, setBounced] = useState("");
  const [exitIntent, setExitIntent] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    const params = { website_id: selectedSite.id, range };
    if (device) params.device = device;
    if (hasRecording) params.has_recording = hasRecording;
    if (bounced) params.bounced = bounced;
    if (exitIntent) params.exit_intent = exitIntent;
    api
      .get("/behavior/sessions/", { params })
      .then((res) => setSessions(res.data.results || res.data))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [selectedSite, range, device, hasRecording, bounced, exitIntent]);

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to view sessions
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Sessions</h2>
        <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                range === r
                  ? "bg-copper/20 text-copper"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <BehaviorNav />

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3">
        <select
          value={device}
          onChange={(e) => setDevice(e.target.value)}
          className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-xs text-gray-300"
        >
          <option value="">All Devices</option>
          <option value="desktop">Desktop</option>
          <option value="mobile">Mobile</option>
          <option value="tablet">Tablet</option>
        </select>
        <select
          value={hasRecording}
          onChange={(e) => setHasRecording(e.target.value)}
          className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-xs text-gray-300"
        >
          <option value="">All Sessions</option>
          <option value="true">Has Recording</option>
          <option value="false">No Recording</option>
        </select>
        <select
          value={bounced}
          onChange={(e) => setBounced(e.target.value)}
          className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-xs text-gray-300"
        >
          <option value="">Bounce: Any</option>
          <option value="true">Bounced</option>
          <option value="false">Not Bounced</option>
        </select>
        <select
          value={exitIntent}
          onChange={(e) => setExitIntent(e.target.value)}
          className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-xs text-gray-300"
        >
          <option value="">Exit Intent: Any</option>
          <option value="true">Exit Intent</option>
          <option value="false">No Exit Intent</option>
        </select>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-2">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-12 bg-dark-300 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-dark-50">
                  <th className="text-left py-2.5 px-4 font-medium">Session ID</th>
                  <th className="text-right py-2.5 px-3 font-medium">Duration</th>
                  <th className="text-right py-2.5 px-3 font-medium">Pages</th>
                  <th className="text-left py-2.5 px-3 font-medium">Entry URL</th>
                  <th className="text-left py-2.5 px-3 font-medium">Device</th>
                  <th className="text-left py-2.5 px-3 font-medium">Country</th>
                  <th className="text-center py-2.5 px-3 font-medium">Bounced</th>
                  <th className="text-center py-2.5 px-3 font-medium">Rec</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s, i) => (
                  <tr
                    key={i}
                    onClick={() =>
                      s.has_recording && navigate(`/behavior/replay/${s.session_id}`)
                    }
                    className={`border-b border-dark-50/50 hover:bg-dark-50/30 ${
                      s.has_recording ? "cursor-pointer" : ""
                    }`}
                  >
                    <td className="py-2.5 px-4 font-mono text-xs text-gray-300">
                      {s.session_id?.substring(0, 8)}...
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {s.duration != null ? `${s.duration.toFixed(0)}s` : "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {s.page_count}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-xs text-gray-400 truncate max-w-[200px]">
                      {s.entry_url}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-400 capitalize">
                      {s.device_type}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-400">
                      {s.country || "—"}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      {s.bounced ? (
                        <span className="text-xs text-red-400">Yes</span>
                      ) : (
                        <span className="text-xs text-gray-500">No</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      {s.has_recording && (
                        <Video size={14} className="text-copper inline-block" />
                      )}
                    </td>
                  </tr>
                ))}
                {!sessions.length && (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-gray-500">
                      No sessions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
